import { Box, type SxProps, type Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { DateTime } from "luxon";
import { type FC, type ReactNode, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CommonDashboardBox } from "../../../components/Dashboard/CommonDashboardBox";
import { CommonDashboardHeader } from "../../../components/Dashboard/CommonDashboardHeader";
import { SystemFlatSwitch } from "../../../components/UIToolkit/Clickable/SystemFlatSwitch";
import { SystemRoundedCheckbox } from "../../../components/UIToolkit/Clickable/SystemRoundedCheckbox";
import { InHouseLineChart } from "../../../components/UIToolkit/Graphs/LineChart/InHouseLineChart";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useDepartmentFlags } from "../../../hooks/useDepartmentFlags";
import { ALPHA, LABOR_MIX } from "../../../theme/tokens";
import { type CostDeepDiveResponse, CostDeepDiveUnit, type OtMode } from "../../../utils/models";
import { formatCurrency } from "../../../utils/utilFunctions";
import { buildClampRange, type ClampRange, costToKDollars, fetchedDataToChartDataGroup } from "../utils";
import { useGetCostDeepDive } from "./hooks";
import { formatHours, hoursToKHours, showsConsecutiveOt } from "./utils";

type BaseProps = {
	laborCost?: boolean;
	label?: string;
	onClick?: () => void;
	trailing?: ReactNode;
	containerSx?: SxProps<Theme>;
	shrinkLegendForSmallScreens?: boolean;
	departmentId?: string;
	/** Controlled unit (overrides internal state). */
	unit?: CostDeepDiveUnit;
	/** Initial unit when uncontrolled. Defaults to "cost". */
	defaultUnit?: CostDeepDiveUnit;
	onUnitChange?: (unit: CostDeepDiveUnit) => void;
	/** When false, hides the $/hrs switch entirely (e.g. no-wage users). */
	canToggleUnit?: boolean;
	/** Selected range; clamps edge weekly buckets so the chart never plots a point outside the selection. */
	clampRange?: ClampRange;
	/**
	 * When true, every value is shown per patient day (value ÷ census) — turning the chart into a
	 * PPD/HPPD breakdown. Controlled by the parent (it also drives the data fetch). The "by census"
	 * checkbox renders only when `onPerCensusChange` is provided.
	 */
	perCensus?: boolean;
	onPerCensusChange?: (next: boolean) => void;
};

type ParentDataProps = BaseProps & {
	data?: CostDeepDiveResponse;
};

type SelfFetchProps = BaseProps & {
	sinceDate: DateTime;
	endDate?: DateTime;
	isActual: boolean;
	roles?: string[];
	jobs?: string[];
	shiftHours?: string[];
	grouping?: boolean;
	excludeHolidays?: boolean;
	includeLateScheduled?: boolean;
	hideWage?: boolean;
	otMode?: OtMode;
	estimateMissingWage: boolean;
	/** The page's `mcSettingsReady`: persisted settings and the Shift Hours scope are both reconciled. */
	ready: boolean;
};

type Props = ParentDataProps | SelfFetchProps;

const isSelfFetchProps = (props: Props): props is SelfFetchProps => "sinceDate" in props;

const CostDeepDiveChartContent: FC<ParentDataProps> = ({
	data,
	laborCost,
	label,
	onClick,
	trailing,
	containerSx,
	shrinkLegendForSmallScreens,
	unit: controlledUnit,
	defaultUnit,
	onUnitChange,
	canToggleUnit,
	clampRange,
	perCensus,
	onPerCensusChange,
}) => {
	const { timezone } = useDepartmentFlags();
	const { t } = useTranslation();
	const { track } = useAnalytics();

	// Controlled vs uncontrolled unit state — mirrors the MUI pattern.
	const [internalUnit, setInternalUnit] = useState<CostDeepDiveUnit>(defaultUnit ?? CostDeepDiveUnit.Cost);
	const unit: CostDeepDiveUnit = controlledUnit ?? internalUnit;
	const handleUnitSwitch = useCallback(
		(next: CostDeepDiveUnit) => {
			track("cost_deep_dive_unit_toggled", { unit: next });
			if (controlledUnit === undefined) setInternalUnit(next);
			onUnitChange?.(next);
		},
		[controlledUnit, onUnitChange, track],
	);

	const handlePerCensusToggle = useCallback(
		(next: boolean) => {
			track("cost_deep_dive_per_census_toggled", { enabled: next });
			onPerCensusChange?.(next);
		},
		[onPerCensusChange, track],
	);

	const hasBonuses = useMemo(() => (data ? Object.values(data.bonusCost).some((v) => v > 0) : false), [data]);
	// DAT-289: keyed on configuration like the table's gate, so the series does not appear and vanish
	// as the user pages between weeks.
	const hasConsecutiveOt = showsConsecutiveOt(data);

	const hasDailyOt = useMemo(() => {
		if (!data) return false;
		if (unit === CostDeepDiveUnit.Hours) return Object.values(data.dailyOtHours ?? {}).some((v) => v > 0);
		return Object.values(data.dailyOtCost).some((v) => v > 0);
	}, [data, unit]);

	const costStackedAreas = !data
		? undefined
		: [
				fetchedDataToChartDataGroup({
					data: data.baseCost,
					name: t("cost.deep.dive.base"),
					timezone,
					clampRange,
					color: LABOR_MIX.BASE,
				}),
				...(hasDailyOt
					? [
							fetchedDataToChartDataGroup({
								data: data.dailyOtCost,
								name: t("cost.deep.dive.daily.ot"),
								timezone,
								clampRange,
								color: LABOR_MIX.DAILY_OT,
							}),
						]
					: []),
				fetchedDataToChartDataGroup({
					data: data.weeklyOtCost,
					name: t("cost.deep.dive.weekly.ot"),
					timezone,
					clampRange,
					color: LABOR_MIX.WEEKLY_OT,
				}),
				...(hasConsecutiveOt
					? [
							fetchedDataToChartDataGroup({
								data: data.consecutiveOtCost ?? {},
								name: t("cost.deep.dive.consecutive.ot"),
								timezone,
								clampRange,
								color: LABOR_MIX.CONSECUTIVE_OT,
							}),
						]
					: []),
				fetchedDataToChartDataGroup({
					data: data.agencyCost,
					name: t("cost.deep.dive.agency"),
					timezone,
					clampRange,
					color: LABOR_MIX.AGENCY,
				}),
				...(hasBonuses
					? [
							fetchedDataToChartDataGroup({
								data: data.bonusCost,
								name: t("cost.deep.dive.bonus"),
								timezone,
								clampRange,
								color: LABOR_MIX.BONUS,
							}),
						]
					: []),
				...(laborCost
					? [
							fetchedDataToChartDataGroup({
								data: data.ptoCost,
								name: t("pto"),
								timezone,
								clampRange,
								color: LABOR_MIX.PTO,
							}),
						]
					: []),
			];

	// Hours mode omits Bonus and PTO (no hour analogue), otherwise mirrors the cost series.
	const hoursStackedAreas = !data
		? undefined
		: [
				fetchedDataToChartDataGroup({
					data: data.baseHours ?? {},
					name: t("cost.deep.dive.hours.base"),
					timezone,
					clampRange,
					color: LABOR_MIX.BASE,
				}),
				...(hasDailyOt
					? [
							fetchedDataToChartDataGroup({
								data: data.dailyOtHours ?? {},
								name: t("cost.deep.dive.hours.daily.ot"),
								timezone,
								clampRange,
								color: LABOR_MIX.DAILY_OT,
							}),
						]
					: []),
				fetchedDataToChartDataGroup({
					data: data.weeklyOtHours ?? {},
					name: t("cost.deep.dive.hours.weekly.ot"),
					timezone,
					clampRange,
					color: LABOR_MIX.WEEKLY_OT,
				}),
				...(hasConsecutiveOt
					? [
							fetchedDataToChartDataGroup({
								data: data.consecutiveOtHours ?? {},
								name: t("cost.deep.dive.hours.consecutive.ot"),
								timezone,
								clampRange,
								color: LABOR_MIX.CONSECUTIVE_OT,
							}),
						]
					: []),
				fetchedDataToChartDataGroup({
					data: data.agencyHours ?? {},
					name: t("cost.deep.dive.hours.agency"),
					timezone,
					clampRange,
					color: LABOR_MIX.AGENCY,
				}),
			];

	const stackedAreas = unit === CostDeepDiveUnit.Hours ? hoursStackedAreas : costStackedAreas;

	const totalLine = !data
		? undefined
		: unit === CostDeepDiveUnit.Hours
			? fetchedDataToChartDataGroup({
					data: data.totalHours ?? {},
					name: t("cost.deep.dive.hours.total"),
					timezone,
					clampRange,
					color: LABOR_MIX.TOTAL,
				})
			: fetchedDataToChartDataGroup({
					data: data.totalCost,
					name: t("cost.deep.dive.total"),
					timezone,
					clampRange,
					color: LABOR_MIX.TOTAL,
				});

	const tooltipSortOrder: Record<string, number> =
		unit === CostDeepDiveUnit.Hours
			? {
					[t("cost.deep.dive.hours.total")]: 0,
					[t("cost.deep.dive.hours.base")]: 1,
					...(hasDailyOt ? { [t("cost.deep.dive.hours.daily.ot")]: 2 } : {}),
					[t("cost.deep.dive.hours.weekly.ot")]: 3,
					[t("cost.deep.dive.hours.agency")]: 4,
				}
			: {
					[t("cost.deep.dive.total")]: 0,
					[t("cost.deep.dive.base")]: 1,
					...(hasDailyOt ? { [t("cost.deep.dive.daily.ot")]: 2 } : {}),
					[t("cost.deep.dive.weekly.ot")]: 3,
					[t("cost.deep.dive.agency")]: 4,
					...(hasBonuses ? { [t("cost.deep.dive.bonus")]: 5 } : {}),
					...(laborCost ? { [t("pto")]: 6 } : {}),
				};

	const tooltipLabelFormatter = (d: DateTime) => d.toFormat("EEEE, MMMM d");

	// Per-census mode shows per-patient-day values (PPD/HPPD), which are small numbers — format as
	// plain hours / dollars rather than the $K / hrK axis used for raw totals.
	const tooltipValueFormatter =
		unit === CostDeepDiveUnit.Hours ? (v: number) => formatHours(v, perCensus ? 2 : 1) : (v: number) => formatCurrency(v, perCensus ? 2 : 1);

	const tickRenderer =
		unit === CostDeepDiveUnit.Hours
			? perCensus
				? (value: number) => formatHours(value, 2)
				: (value: number) => hoursToKHours(value)
			: perCensus
				? (value: number) => formatCurrency(value, 0)
				: (value: number) => costToKDollars(value) ?? "$0.0K";

	const unitSwitch =
		canToggleUnit === false ? null : (
			// stopPropagation so the card's onClick (navigate to detail) doesn't fire when toggling units
			<Box onClick={(e) => e.stopPropagation()} data-testid="cost-deep-dive-unit-toggle">
				<SystemFlatSwitch
					options={[
						{ label: t("cost.deep.dive.unit.cost"), value: CostDeepDiveUnit.Cost },
						{ label: t("cost.deep.dive.unit.hours"), value: CostDeepDiveUnit.Hours },
					]}
					activeIndexOrValue={unit}
					onSwitch={(option) => handleUnitSwitch(option.value)}
				/>
			</Box>
		);

	const censusCheckbox = onPerCensusChange ? (
		// stopPropagation so the card's onClick (navigate to detail) doesn't fire when toggling
		<Box onClick={(e) => e.stopPropagation()} data-testid="cost-deep-dive-census-toggle">
			<SystemRoundedCheckbox
				checked={!!perCensus}
				onChange={handlePerCensusToggle}
				label={t("cost.deep.dive.by.census")}
				data-testid="cost-deep-dive-census-checkbox"
			/>
		</Box>
	) : null;

	const headerTrailing =
		unitSwitch || censusCheckbox ? (
			<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
				{censusCheckbox}
				{unitSwitch}
				{trailing}
			</Box>
		) : (
			trailing
		);

	return (
		<CommonDashboardBox
			onClick={onClick}
			sx={
				((theme) => ({
					display: "flex",
					flexDirection: "column",
					position: "relative",
					width: "calc(33% - 4px)",
					alignItems: "start",
					gap: 1,
					...containerSx,
					...(onClick && {
						cursor: "pointer",
						"&:hover": { boxShadow: `0px 2px 12px 0px ${alpha(theme.palette.primary.main, ALPHA.FOCUS_RING)}` },
						transition: "box-shadow 0.2s",
					}),
				})) as SxProps<Theme>
			}
		>
			{label ? (
				<CommonDashboardHeader label={label} trailing={headerTrailing} />
			) : (
				headerTrailing && <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%", px: 1 }}>{headerTrailing}</Box>
			)}
			<InHouseLineChart
				shrinkLegendForSmallScreens={shrinkLegendForSmallScreens}
				margin={{
					top: 4,
					right: 16,
					bottom: 0,
					left: 20,
				}}
				width="100%"
				height={containerSx ? "100%" : undefined}
				tooltipSortOrder={tooltipSortOrder}
				tooltipValueFormatter={tooltipValueFormatter}
				tooltipLabelFormatter={tooltipLabelFormatter}
				leftAxisData={{
					dataGroups: totalLine ? [totalLine] : undefined,
					stackedAreaGroups: stackedAreas ? [stackedAreas] : undefined,
					minValue: 0,
					tickRenderer,
				}}
			/>
		</CommonDashboardBox>
	);
};

const SelfFetchingChart: FC<SelfFetchProps> = ({
	sinceDate,
	endDate,
	isActual,
	roles,
	jobs,
	shiftHours,
	excludeHolidays,
	includeLateScheduled,
	departmentId,
	hideWage,
	otMode,
	estimateMissingWage,
	ready,
	...rest
}) => {
	const { timezone } = useDepartmentFlags();
	// This card self-fetches, so the readiness gate has to be applied here rather than by a caller:
	// `estimateMissingWage` and the role/Shift Hours scope are part of the query key, and firing before
	// they settle would paint one curve and then silently correct it. Same gate as
	// `visualizers/ScheduledVsActualCost.tsx`. <!-- APP-731 -->
	const { data } = useGetCostDeepDive({
		sinceDate,
		endDate,
		isActual,
		grouping: true,
		roles,
		jobs,
		laborCost: rest.laborCost,
		shiftHours,
		excludeHolidays,
		includeLateScheduled,
		departmentId,
		hideWage,
		perCensus: rest.perCensus,
		otMode,
		estimateMissingWage,
		enabled: ready,
	});
	const clampRange = useMemo(() => buildClampRange(sinceDate, endDate, timezone), [sinceDate, endDate, timezone]);
	return <CostDeepDiveChartContent data={data} clampRange={clampRange} {...rest} />;
};

export const CostDeepDiveChart: FC<Props> = (props) => {
	if (isSelfFetchProps(props)) return <SelfFetchingChart {...props} />;
	return <CostDeepDiveChartContent {...props} />;
};
